/* RD Customz — homepage behaviour. Loaded on the index template only (rdc-head-assets).

   Hero slideshow (owner 17-09-2026). Slide 1 is the LCP image and is already painted; the others are display:none in the HTML, so the
   browser downloads nothing for them before photo 1 has loaded. The clock starts as soon as photo 1 is on screen (not at the window load
   event: on phones that came 4–10 s later, so the first switch felt like it never happened); 4 s per photo below 1024 px, 6 s above.
   Each slide is loaded and decoded before it fades in, and the next one is warmed one step ahead. Autoplay pauses on mouse hover,
   keyboard focus, a hidden tab and the pause button. With prefers-reduced-motion it keeps running: a cross-fade is the reduced-motion
   alternative (Apple HIG), and the pause button stays (WCAG 2.2.2). */

const hero = document.querySelector('[data-rdc-hero-slides]');
const slides = hero ? [...hero.querySelectorAll('[data-rdc-slide]')] : [];

if (slides.length > 1) {
  const dots = [...hero.querySelectorAll('[data-rdc-slide-dot]')];
  const toggle = hero.querySelector('[data-rdc-slide-toggle]');
  const small = window.matchMedia('(max-width: 1023px)');
  const delay = () => (Number(small.matches ? hero.dataset.intervalMobile : hero.dataset.interval) || (small.matches ? 4 : 6)) * 1000;
  let current = 0;
  let timer = 0;
  let busy = false;
  let paused = false;
  let held = false;   // hover or focus inside the hero

  // Waits for the next load/error of an image, at most `ms`: a slide can never hold the slideshow (18-10-2026: an image that had
  // already failed never fired again, and the slideshow waited for it for good).
  const settle = (img, ms) => new Promise((done) => {
    const t = setTimeout(done, ms);
    const end = () => { clearTimeout(t); done(); };
    img.addEventListener('load', end, { once: true });
    img.addEventListener('error', end, { once: true });
  });

  const load = async (slide) => {
    slide.hidden = false;
    const img = slide.querySelector('img');
    if (!img) return;
    img.loading = 'eager';
    if (!img.complete) await settle(img, 8000);
    if (!img.naturalWidth && slide.querySelector('source')) {
      // a pre-encoded AVIF that is not in Settings → Files: drop it, the browser falls back to the Shopify image (WebP/JPEG)
      slide.querySelectorAll('source').forEach((s) => s.remove());
      await settle(img, 8000);
    }
    if (img.decode) await img.decode().catch(() => {});
  };

  const schedule = () => {
    clearTimeout(timer);
    if (!paused && !held && !document.hidden) timer = setTimeout(() => show((current + 1) % slides.length), delay());
  };

  const show = async (n) => {
    if (busy || n === current) return schedule();
    busy = true;
    await load(slides[n]);
    slides[current].classList.remove('is-active');
    slides[n].classList.add('is-active');
    dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === n)));
    current = n;
    busy = false;
    load(slides[(n + 1) % slides.length]);
    schedule();
  };

  const setPaused = (value) => {
    paused = value;
    if (toggle) {
      toggle.toggleAttribute('data-paused', paused);
      toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    }
    schedule();
  };

  dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
  toggle?.addEventListener('click', () => setPaused(!paused));
  hero.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { held = true; schedule(); } });
  hero.addEventListener('pointerleave', () => { held = false; schedule(); });
  // keyboard focus only: a tapped dot keeps focus on Android and would otherwise stop autoplay for good
  hero.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) { held = true; schedule(); } });
  hero.addEventListener('focusout', (e) => { if (!hero.contains(e.relatedTarget)) { held = false; schedule(); } });
  document.addEventListener('visibilitychange', schedule);

  const first = slides[0].querySelector('img');
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    setPaused(paused);
    load(slides[1]);
  };
  if (!first || (first.complete && first.naturalWidth)) start();
  else {
    first.addEventListener('load', start, { once: true });
    first.addEventListener('error', start, { once: true });
    window.addEventListener('load', start, { once: true });
  }
}
